import { Document, Image, Page, Path, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import { DesignProps } from 'api/DesignApi';
import { Message } from 'api/MessagesApi';
import { isEternalOnlyBranding } from 'utils/branding';
import { ensureHttp, getFullNameOrEmail } from 'utils/utils';

type MessagePdfProps = {
  message: Message;
  designProps?: DesignProps;
};

type MessagePdfTheme = {
  isPartnerBranded: boolean;
  companyName: string;
  titleColor: string;
  dividerColor: string;
  iconColor: string;
  noteBackground: string;
  noteBorderColor: string;
  noteTextColor: string;
  footerBackground: string;
  footerAccentColor: string;
  footerTextColor: string;
  footerMeta: string[];
  calloutHeading: string;
  calloutBody: string;
};

export type MessagePdfModel = {
  deliveredOnText: string;
  fromName: string;
  toName: string;
  paragraphs: string[];
  domainUrl: string;
  domainLabel: string;
  partnerLogoUrl: string;
  theme: MessagePdfTheme;
};

const ETERNAL_COMPANY_NAME = 'Eternal Applications';
const ETERNAL_DOMAIN_URL = 'https://www.eternalapplications.com';
const ETERNAL_DOMAIN_LABEL = 'eternalapplications.com';
const ETERNAL_TAGLINE = 'Connecting hearts. Preserving legacies.';
const ETERNAL_TITLE_COLOR = '#24364b';
const ETERNAL_DIVIDER_COLOR = '#9abfde';
const ETERNAL_NOTE_BACKGROUND = '#eef6fc';
const ETERNAL_NOTE_BORDER = '#d7e8f5';
const ETERNAL_NOTE_TEXT = '#41566f';
const ETERNAL_FOOTER_BACKGROUND = '#24364b';
const PARTNER_FALLBACK_COLOR = '#9d3041';
const DEFAULT_RECIPIENT_NAME = '';

const styles = StyleSheet.create({
  page: {
    padding: 16,
    backgroundColor: '#f4f4f2',
    fontFamily: 'Times-Roman',
    color: '#1f1f1f'
  },
  sheet: {
    backgroundColor: '#ffffff',
    minHeight: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  mainContent: {
    flexGrow: 1,
    paddingTop: 34,
    paddingHorizontal: 34,
    paddingBottom: 18
  },
  hero: {
    alignItems: 'center',
    marginBottom: 20
  },
  heroLogo: {
    maxWidth: 160,
    maxHeight: 52,
    marginTop: 4,
    marginBottom: 8,
    objectFit: 'contain'
  },
  title: {
    fontFamily: 'Times-Bold',
    fontSize: 26,
    marginBottom: 6
  },
  deliveredOn: {
    fontSize: 11,
    marginBottom: 12
  },
  divider: {
    height: 1.5,
    marginBottom: 16
  },
  partiesRow: {
    marginBottom: 18
  },
  partyBlock: {
    width: '100%'
  },
  partyLine: {
    fontSize: 11.5,
    lineHeight: 1.45
  },
  partyLabel: {
    fontFamily: 'Times-Bold'
  },
  messageBody: {
    flexGrow: 1,
    marginBottom: 18
  },
  paragraph: {
    fontSize: 11,
    lineHeight: 1.55,
    marginBottom: 12
  },
  calloutIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12
  }
});

const isValidHexColor = (color?: string | null) => !!color && /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(color.trim());

const normalizeHexColor = (color: string) => {
  const trimmedColor = color.trim();
  if (trimmedColor.length === 4) {
    return `#${trimmedColor
      .slice(1)
      .split('')
      .map((char) => `${char}${char}`)
      .join('')}`;
  }

  return trimmedColor;
};

const toAlphaColor = (hexColor: string, alpha: number) => {
  const normalizedHex = normalizeHexColor(hexColor).replace('#', '');
  const red = parseInt(normalizedHex.slice(0, 2), 16);
  const green = parseInt(normalizedHex.slice(2, 4), 16);
  const blue = parseInt(normalizedHex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const parseMessagePdfDate = (message: Message) => {
  const { deliveryYear, deliveryMonth, deliveryDay } = message;

  if (deliveryYear && deliveryMonth && deliveryDay) {
    const deliveredAt = new Date(deliveryYear, deliveryMonth - 1, deliveryDay);
    const isExactMatch =
      deliveredAt.getFullYear() === deliveryYear &&
      deliveredAt.getMonth() === deliveryMonth - 1 &&
      deliveredAt.getDate() === deliveryDay;

    if (!Number.isNaN(deliveredAt.getTime()) && isExactMatch) {
      return deliveredAt;
    }
  }

  const createdAt = new Date(message.createdAt);
  return Number.isNaN(createdAt.getTime()) ? undefined : createdAt;
};

export const formatMessagePdfDate = (message: Message, fallbackDate = new Date()) => {
  const resolvedDate = parseMessagePdfDate(message) || fallbackDate;

  return resolvedDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

export const getMessagePdfParagraphs = (content?: string) => {
  const trimmedContent = content?.trim() || '';
  if (!trimmedContent) {
    return [];
  }

  return trimmedContent
    .split(/\n\s*\n/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

const getMessagePdfSenderName = (message: Message) => {
  const userSenderIdentity = message.user ? getFullNameOrEmail(message.user, '') : '';
  const patientProfileSenderIdentity = message.patientProfile
    ? `${message.patientProfile.firstName || ''} ${message.patientProfile.lastName || ''}`.trim() || message.patientProfile.email || ''
    : '';

  return userSenderIdentity.trim() || patientProfileSenderIdentity.trim() || 'Unknown';
};

const getMessagePdfRecipientName = (message: Message) => {
  const contacts = Array.isArray(message.contacts) ? message.contacts : [];
  const primaryContact = contacts.find((contact) => contact.name?.trim() || contact.email?.trim());

  return primaryContact?.name?.trim() || primaryContact?.email?.trim() || DEFAULT_RECIPIENT_NAME;
};

const getMessagePdfDomainLabel = (domainUrl: string) => {
  try {
    return new URL(domainUrl).host || ETERNAL_DOMAIN_LABEL;
  } catch {
    return domainUrl.replace(/^https?:\/\//i, '').replace(/\/.*$/, '') || ETERNAL_DOMAIN_LABEL;
  }
};

const buildMessagePdfTheme = (designProps?: DesignProps): MessagePdfTheme => {
  const companyName = designProps?.companyName?.trim() || ETERNAL_COMPANY_NAME;
  const useEternalBranding = isEternalOnlyBranding(companyName);

  if (useEternalBranding) {
    return {
      isPartnerBranded: false,
      companyName: ETERNAL_COMPANY_NAME,
      titleColor: ETERNAL_TITLE_COLOR,
      dividerColor: ETERNAL_DIVIDER_COLOR,
      iconColor: ETERNAL_DIVIDER_COLOR,
      noteBackground: ETERNAL_NOTE_BACKGROUND,
      noteBorderColor: ETERNAL_NOTE_BORDER,
      noteTextColor: ETERNAL_NOTE_TEXT,
      footerBackground: ETERNAL_FOOTER_BACKGROUND,
      footerAccentColor: ETERNAL_DIVIDER_COLOR,
      footerTextColor: '#ffffff',
      footerMeta: [ETERNAL_COMPANY_NAME.toUpperCase(), ETERNAL_TAGLINE, ETERNAL_DOMAIN_LABEL],
      calloutHeading: 'This message was securely delivered through Eternal Applications.',
      calloutBody: 'Thank you for being a part of an everlasting connection.'
    };
  }

  const primaryColor = isValidHexColor(designProps?.colorPrimary) ? normalizeHexColor(designProps!.colorPrimary!) : PARTNER_FALLBACK_COLOR;
  const secondaryColor = isValidHexColor(designProps?.colorPrimaryLighter)
    ? normalizeHexColor(designProps!.colorPrimaryLighter!)
    : primaryColor;
  const domainLabel = designProps?.domainUrl
    ? getMessagePdfDomainLabel(ensureHttp(designProps.domainUrl) || ETERNAL_DOMAIN_URL)
    : ETERNAL_DOMAIN_LABEL;
  const footerMeta = [
    domainLabel,
    designProps?.email?.trim() || companyName,
    designProps?.footerText?.trim() || `Delivered with care by ${companyName}`
  ];

  return {
    isPartnerBranded: true,
    companyName,
    titleColor: primaryColor,
    dividerColor: primaryColor,
    iconColor: primaryColor,
    noteBackground: toAlphaColor(primaryColor, 0.09),
    noteBorderColor: toAlphaColor(primaryColor, 0.18),
    noteTextColor: '#4a2a31',
    footerBackground: primaryColor,
    footerAccentColor: secondaryColor,
    footerTextColor: '#ffffff',
    footerMeta,
    calloutHeading: `This message was securely delivered through ${companyName}.`,
    calloutBody: 'Thank you for being a part of our community and mission of compassionate care.'
  };
};

export const buildMessagePdfModel = (message: Message, designProps?: DesignProps, fallbackDate = new Date()): MessagePdfModel => {
  const theme = buildMessagePdfTheme(designProps);
  const domainUrl = ensureHttp(designProps?.domainUrl) || ETERNAL_DOMAIN_URL;
  const domainLabel = designProps?.domainUrl ? getMessagePdfDomainLabel(domainUrl) : ETERNAL_DOMAIN_LABEL;
  const partnerLogoUrl = theme.isPartnerBranded ? ensureHttp(designProps?.logoUrl?.trim()) || '' : '';

  return {
    deliveredOnText: `Delivered on ${formatMessagePdfDate(message, fallbackDate)}`,
    fromName: getMessagePdfSenderName(message),
    toName: getMessagePdfRecipientName(message),
    paragraphs: getMessagePdfParagraphs(message.content),
    domainUrl,
    domainLabel,
    partnerLogoUrl,
    theme
  };
};

function MessageDocumentBadge({ color }: { color: string }) {
  return (
    <View style={[styles.calloutIconWrap, { backgroundColor: color, marginRight: 0 }]}>
      <Svg width={18} height={22} viewBox="0 0 18 22">
        <Path d="M4 1.5H10L14.5 6V20.5H4Z" fill="none" stroke="#ffffff" strokeWidth={1.4} />
        <Path d="M10 1.5V6H14.5" fill="none" stroke="#ffffff" strokeWidth={1.4} />
        <Path d="M6.2 10H12.3" fill="none" stroke="#ffffff" strokeWidth={1.2} />
        <Path d="M6.2 13.3H12.3" fill="none" stroke="#ffffff" strokeWidth={1.2} />
        <Path d="M6.2 16.6H11" fill="none" stroke="#ffffff" strokeWidth={1.2} />
      </Svg>
    </View>
  );
}

export default function MessagePdf({ message, designProps }: MessagePdfProps) {
  const model = buildMessagePdfModel(message, designProps);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.sheet}>
          <View style={styles.mainContent}>
            <View style={styles.hero}>
              {model.partnerLogoUrl ? (
                <Image src={model.partnerLogoUrl} style={styles.heroLogo} />
              ) : (
                <MessageDocumentBadge color={model.theme.iconColor} />
              )}
              <Text style={[styles.title, { color: model.theme.titleColor }]}>Legacy Message</Text>
              <Text style={styles.deliveredOn}>{model.deliveredOnText}</Text>
            </View>

            <View style={[styles.divider, { backgroundColor: model.theme.dividerColor }]} />

            <View style={styles.partiesRow}>
              <View style={styles.partyBlock}>
                <Text style={styles.partyLine}>
                  <Text style={[styles.partyLabel, { color: model.theme.titleColor }]}>From: </Text>
                  <Text>{model.fromName}</Text>
                </Text>
              </View>
            </View>

            <View style={styles.messageBody}>
              {model.paragraphs.length > 0 ? (
                model.paragraphs.map((paragraph, index) => (
                  <Text key={`${paragraph.slice(0, 20)}-${index}`} style={styles.paragraph}>
                    {paragraph}
                  </Text>
                ))
              ) : (
                <Text style={styles.paragraph}>{message.content?.trim() || ''}</Text>
              )}
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}
